import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Box from '@mui/material/Box';
import { Subscription } from '@rails/actioncable';
import { useQueryClient } from '@tanstack/react-query';
import { InvoiceCategoryFilter, Order_Read } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExpandedStateButton from '~components/ExpandedStateButton/ExpandedStateButton';
import {
  LoadingSpinner,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FetchReason } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Order } from '~hooks/useOrders/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store/RootStore';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';
import { usePrevious } from '~utils/hooks/usePrevious';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { DriverDayDateFilter } from '../DriverPay/DriverPayDateFilter';
import { TabQSParam } from '../DriverPay/TabQSParam';
import BulkActionsBar from './components/BulkActionsBar/BulkActionsBar';
import BulkCreateSettlementCTA from './components/CTAs/BulkCreateSettlementCTA';
import OrderSelectorCTA from './components/CTAs/OrderSelectorCTA';
import SettlementsOrderSelectorCTA from './components/CTAs/SettlementsOrderSelectorCTA';
import DriverPayFiltersV2 from './components/DriverPayTabs';
import DateRangeFilter from './components/Filters/DateRangeFilter';
import InvoiceStateFilters from './components/Filters/InvoiceStateFilters';
import OrdersFilters from './components/Filters/OrdersFilters';
import Search from './components/Filters/Search';
import SettlementsFilters from './components/Filters/SettlementsFilters';
import SettlementsSearch from './components/Filters/SettlementsSearch';
import SettlementStatusFilters from './components/Filters/SettlementStatusFilters';
import OrdersList from './components/OrdersList/OrdersList';
import OrdersListFooter from './components/OrdersListFooter';
import { GridContainer, GridRow } from './components/styled';
import { ORDERS_LIST_CONTAINER_VERTICAL_GAP } from './constants';
import DriverPayPageContext, {
  DriverPayPageContextProvider,
} from './DriverPayPageContext';
import DriverPayQueryContext, {
  deriveInvoiceCsvExportQueryParams,
  DriverPayQueryContextProvider,
} from './DriverPayQueryContext';
import DriverPaySettlements from './DriverPaySettlements';
import {
  deriveExportButtonLabelKey,
  deriveExportCategory,
  makeDriverPayQueryContextKey,
} from './utils';
const infiniteScrollOptions: IntersectionObserverInit = {
  threshold: 0.1,
  rootMargin: '500px',
} as const;

export default observer(function DriverPayContainer() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  return (
    <DriverPayQueryContextProvider companyId={companyId}>
      <DriverPayPageContextProvider>
        <DriverPay />
      </DriverPayPageContextProvider>
    </DriverPayQueryContextProvider>
  );
});

const DriverPay = observer(() => {
  const driverPayQuery = useContext(DriverPayQueryContext);
  const {
    currentCategory,
    orderCardsExpandedState: { allCards },
    toggleAllOrderCardsExpandedState,
    uncheckAllInvoices,
  } = useContext(DriverPayPageContext);
  const infiniteScroll = useInfiniteScroll(infiniteScrollOptions);
  const { exportInvoicesCsv } = useInvoices();
  const { exportSettlementsCsv } = useSettlements();
  const [isExporting, setIsExporting] = useState(false);

  const exportCategory = deriveExportCategory(driverPayQuery.filters.invoiceCategory);
  const exportButtonLabel = t(deriveExportButtonLabelKey(exportCategory));

  const handleExportInvoices = useCallback(async () => {
    setIsExporting(true);
    try {
      const invoiceFilters = deriveInvoiceCsvExportQueryParams(driverPayQuery.filters);
      await exportInvoicesCsv(exportCategory, invoiceFilters);
    } finally {
      setIsExporting(false);
    }
  }, [exportInvoicesCsv, driverPayQuery.filters, exportCategory]);

  const handleExportSettlements = useCallback(async () => {
    setIsExporting(true);
    try {
      const category =
        driverPayQuery.filters.invoiceCategory ?? InvoiceCategoryFilter.PAYABLES;
      await exportSettlementsCsv(category);
    } finally {
      setIsExporting(false);
    }
  }, [exportSettlementsCsv, driverPayQuery.filters.invoiceCategory]);

  const [ordersRTUSubscription, setOrdersRTUSubscription] = useState<Subscription>();
  const queryClient = useQueryClient();

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const previousFetchReason = usePrevious(driverPayQuery.fetchReason);
  const fetchMoreFinished =
    previousFetchReason === FetchReason.INFINITE_SCROLL && !driverPayQuery.fetchReason;

  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab');
  const {
    isAwaitingTreatment: isAwaitingChargeBasedSettlements,
    isFeatureFlagEnabled: chargeBasedSettlements,
  } = useAwaitableFeatureFlag({ featureFlagKey: FeatureFlags.chargeBasedSettlements });
  const isSettlementPage =
    chargeBasedSettlements || selectedTab === TabQSParam.SETTLEMENTS;

  const subscribeToOrdersRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.CompanyOrderUpdateChannel,
      companyId,
      setOrdersRTUSubscription,
      ({ data }: { data: Order_Read }) => {
        const parsedOrder = Order.parse(data);
        const queryKey = makeDriverPayQueryContextKey();

        queryClient.setQueriesData<{ pages: Array<Order[]> | undefined }>(
          { queryKey: [queryKey] },
          (data) => {
            if (data?.pages) {
              const newPages = data.pages.map((page) => {
                const cachedOrderIndex = page.findIndex((order) => {
                  return order.id === parsedOrder.id;
                });

                if (cachedOrderIndex >= 0) {
                  const newPage = [...page];
                  newPage.splice(cachedOrderIndex, 1, parsedOrder);

                  return newPage;
                }

                return page;
              });

              return { ...data, pages: newPages };
            }

            return data;
          },
        );
      },
    );
  }, [companyId]);

  useEffect(() => {
    uncheckAllInvoices();
    driverPayQuery.applyFilters({ invoiceCategory: currentCategory });
  }, [currentCategory]);

  useEffect(() => {
    if (fetchMoreFinished) {
      infiniteScroll.notify();
    }
  }, [fetchMoreFinished]);

  useEffect(() => {
    if (infiniteScroll.endReached) {
      driverPayQuery.fetchMore();
    }
  }, [infiniteScroll.endReached, driverPayQuery.fetchMore]);

  useEffect(() => {
    return () => {
      driverPayQuery.refresh();
    };
  }, [companyId]);

  useEffect(() => {
    return () => {
      driverPayQuery.clear();
    };
  }, []);

  useEffect(() => {
    if (!ordersRTUSubscription) {
      subscribeToOrdersRTU();
    }
    return () => {
      ordersRTUSubscription?.unsubscribe();
    };
  }, [ordersRTUSubscription, subscribeToOrdersRTU]);

  if (isAwaitingChargeBasedSettlements) {
    return <LoadingSpinner isVisible />;
  }

  return (
    <>
      <GridContainer>
        <GridRow sx={{ mt: '-1px' }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
            }}
          >
            {isSettlementPage ? (
              <>
                <DriverDayDateFilter />
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <SmallLoadingButton
                    loading={isExporting}
                    onClick={handleExportSettlements}
                    startIcon={<FileDownloadOutlined />}
                    variant="outlined"
                    color="secondary"
                  >
                    {t('approvals.export.export_csv')}
                  </SmallLoadingButton>
                  <SettlementsOrderSelectorCTA />
                </Box>
              </>
            ) : (
              <>
                <DateRangeFilter />

                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <SmallLoadingButton
                    loading={isExporting}
                    onClick={handleExportInvoices}
                    startIcon={<FileDownloadOutlined />}
                    variant="outlined"
                    color="secondary"
                  >
                    {exportButtonLabel}
                  </SmallLoadingButton>
                  <BulkCreateSettlementCTA />
                  <ExpandedStateButton
                    onChange={toggleAllOrderCardsExpandedState}
                    state={allCards}
                  />
                  <OrderSelectorCTA />
                </Box>
              </>
            )}
          </Box>
        </GridRow>

        <GridRow>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            {!chargeBasedSettlements && <DriverPayFiltersV2 />}

            {isSettlementPage ? (
              <>
                <Box sx={{ minWidth: '180px', flex: 0 }}>
                  <SettlementsSearch />
                </Box>

                <SettlementsFilters />
                <SettlementStatusFilters />
              </>
            ) : (
              <>
                <Box sx={{ minWidth: '180px', flex: 0 }}>
                  <Search />
                </Box>

                <OrdersFilters />

                <InvoiceStateFilters />
              </>
            )}
          </Box>
        </GridRow>

        {isSettlementPage && (
          <GridRow>
            <DriverPaySettlements />
          </GridRow>
        )}

        {!isSettlementPage && (
          <GridRow
            sx={(theme) => ({
              bgcolor: 'transparent',
              borderTopColor: theme.brandV2.colors.treadGray7,
              overflow: 'auto',
              py: ORDERS_LIST_CONTAINER_VERTICAL_GAP,
            })}
          >
            <LoadingSpinner
              isVisible={driverPayQuery.fetchReason === FetchReason.INITIAL_FETCH}
            />

            <OrdersList orders={driverPayQuery.data} />

            {driverPayQuery.hasMore && (
              <Box ref={infiniteScroll.nodeRef} sx={{ mt: 1.5 }}>
                <LoadingSpinner
                  isVisible={driverPayQuery.fetchReason === FetchReason.INFINITE_SCROLL}
                />
              </Box>
            )}

            <OrdersListFooter />
          </GridRow>
        )}
      </GridContainer>

      <BulkActionsBar />
    </>
  );
});
